import { z } from "zod";
import {
  CHANNELS,
  DELIVERY_EVENT_TYPES,
  DELIVERY_STATUSES,
  LIMITS,
  MESSAGE_PRIORITIES,
  MESSAGE_STATUSES,
  MESSAGE_STRATEGIES,
  PUSH_CHANNELS,
} from "../constants";
import { DELIVERY_ISSUES } from "../issues";
import { IsoDate, paginated, Uuid } from "./common";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const CredentialAlias = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z0-9][a-z0-9._-]*$/,
    "Alias may only contain lowercase letters, digits, dot, underscore and dash",
  )
  .meta({ description: "Alias of a credential assigned to the app (D-05).", example: "email-main" });

export const PhoneE164 = z
  .string()
  .regex(/^\+[1-9]\d{6,14}$/, "Phone number must be in E.164 format, e.g. +6281234567890")
  .meta({ example: "+6281234567890" });

export const Category = z
  .string()
  .regex(/^[a-z][a-z0-9_-]{0,63}$/)
  .meta({ description: "Message category. Defaults to `transactional` (D-22).", example: "marketing" });

// ---------------------------------------------------------------------------
// Channel payloads
// ---------------------------------------------------------------------------

/**
 * Data keys FCM keeps for itself. A message using one is rejected as a whole, so it is refused at request time
 * instead of failing in the worker.
 */
const FCM_RESERVED_DATA_KEY = /^(?:from|message_type|gcm)$|^google|^gcm\./;

export const FcmPayload = z
  .strictObject({
    title: z.string().max(250).optional(),
    body: z.string().max(4000).optional(),
    image: z.url().optional().meta({
      description:
        "Image shown in the notification. JPEG, PNG and BMP work everywhere; Android limits it to 1 MB.",
    }),
    data: z
      .record(z.string().min(1), z.string())
      .superRefine((data, ctx) => {
        for (const key of Object.keys(data)) {
          if (FCM_RESERVED_DATA_KEY.test(key)) {
            ctx.addIssue({ code: "custom", path: [key], message: `Data key '${key}' is reserved by FCM` });
          }
        }
      })
      .optional()
      .meta({
        description:
          "Key-value pairs passed to the app. Values must be strings. Keys reserved by FCM (`from`, `message_type`, `gcm`, `google*`, `gcm.*`) are rejected.",
      }),
    click_action: z
      .string()
      .max(255)
      .optional()
      .meta({ description: "Android: intent action when the notification is tapped." }),
    android_channel_id: z.string().max(255).optional().meta({
      description:
        "Android: notification channel created by the app. Unknown channels fall back to the app default.",
    }),
    sound: z.string().max(255).optional().meta({
      description: "Android: `default` or the name of a sound file in the app's `res/raw`.",
    }),
    tag: z.string().max(255).optional().meta({
      description: "Android and web: a notification with the same tag replaces the one already shown.",
    }),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/, "Color must be #rrggbb")
      .optional()
      .meta({ description: "Android: notification icon color as `#rrggbb`.", example: "#0F62FE" }),
    notification_count: z.int().min(0).max(2_147_483_647).optional().meta({
      description:
        "Android: number of items this notification stands for, shown as a badge by launchers that support it.",
    }),
    visibility: z.enum(["private", "public", "secret"]).optional().meta({
      description:
        "Android lock screen: `private` (default) hides the content on a secure lock screen, `public` shows it, `secret` hides the whole notification.",
    }),
    collapse_key: z.string().max(255).optional().meta({
      description:
        "Android: while the device is offline only the last message with this key is kept. At most 4 different keys at a time.",
    }),
    priority: z.enum(["normal", "high"]).optional().meta({
      description:
        "`high` wakes the device right away (Android priority, web `Urgency`). Use it only for messages the user must see now.",
    }),
    ttl_sec: z.int().min(0).max(2_419_200).optional().meta({
      description:
        "How long FCM keeps the message while the device is offline, up to 4 weeks. `0` means now or never.",
    }),
    link: z
      .url({ protocol: /^https$/, error: "link must be an HTTPS URL" })
      .optional()
      .meta({ description: "Web: HTTPS URL opened when the notification is clicked." }),
    analytics_label: z
      .string()
      .regex(/^[a-zA-Z0-9\-_.~%]{1,50}$/, "Up to 50 characters of letters, digits and -_.~%")
      .optional()
      .meta({
        description: "Label for this message in Firebase Analytics delivery reports.",
        example: "promo-oct",
      }),
  })
  .refine((p) => p.title || p.body || (p.data && Object.keys(p.data).length > 0), {
    message: "FCM payload needs at least one of title, body or data",
  })
  .meta({
    id: "FcmPayload",
    description:
      "Firebase Cloud Messaging for Android and web apps, sent through the HTTP v1 API to each device's installation ID or registration token.",
  });
export type FcmPayload = z.infer<typeof FcmPayload>;

export const ApnsPayload = z
  .strictObject({
    title: z.string().max(250).optional(),
    subtitle: z.string().max(250).optional(),
    body: z.string().max(4000).optional(),
    badge: z.int().min(0).optional(),
    sound: z.string().max(255).optional(),
    category: z.string().max(255).optional(),
    thread_id: z.string().max(255).optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    content_available: z.boolean().optional(),
    mutable_content: z.boolean().optional(),
    collapse_id: z.string().max(64).optional(),
    push_type: z.enum(["alert", "background"]).optional(),
    priority: z.union([z.literal(5), z.literal(10)]).optional(),
    ttl_sec: z.int().min(0).optional(),
  })
  .meta({ id: "ApnsPayload" });

export const WebPushPayload = z
  .strictObject({
    title: z.string().max(250),
    body: z.string().max(4000).optional(),
    icon: z.url().optional(),
    image: z.url().optional(),
    url: z.url().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    urgency: z.enum(["very-low", "low", "normal", "high"]).optional(),
    ttl_sec: z.int().min(0).optional(),
  })
  .meta({ id: "WebPushPayload" });

export const EmailPayload = z
  .strictObject({
    subject: z.string().min(1).max(998),
    text: z.string().max(1_000_000).optional(),
    html: z.string().max(5_000_000).optional(),
    from_name: z
      .string()
      .max(255)
      .optional()
      .meta({ description: "Overrides the sender name from the credential." }),
    reply_to: z.email().optional(),
  })
  .refine((p) => p.text || p.html, { message: "Email payload needs text or html" })
  .meta({ id: "EmailPayload" });
export type EmailPayload = z.infer<typeof EmailPayload>;

export const SmsPayload = z.strictObject({ text: z.string().min(1).max(1600) }).meta({ id: "SmsPayload" });

export const WhatsappPayload = z
  .strictObject({
    template: z.string().min(1).max(512).meta({ description: "Name of a template approved by Meta." }),
    language: z.string().min(2).max(15).default("id"),
    variables: z
      .array(z.string())
      .max(50)
      .optional()
      .meta({ description: "Template body parameters, in order." }),
    header: z
      .strictObject({
        type: z.enum(["text", "image", "document", "video"]),
        value: z.string().min(1),
      })
      .optional(),
  })
  .meta({ id: "WhatsappPayload" });

// ---------------------------------------------------------------------------
// Channel items
// ---------------------------------------------------------------------------

const credentialField = CredentialAlias.optional().meta({
  description:
    "Credential alias. When empty, the highest priority credential for this channel in the app is used.",
});
const allowFallbackField = z.boolean().optional().meta({
  description:
    "Allow switching to another credential when the selected one is unavailable. Defaults to `false` when `credential` is set, `true` otherwise.",
});

export const FcmChannel = z
  .strictObject({ type: z.literal("fcm"), credential: credentialField, payload: FcmPayload })
  .meta({ id: "FcmChannel" });
export const ApnsChannel = z
  .strictObject({ type: z.literal("apns"), credential: credentialField, payload: ApnsPayload })
  .meta({ id: "ApnsChannel" });
export const WebPushChannel = z
  .strictObject({ type: z.literal("webpush"), credential: credentialField, payload: WebPushPayload })
  .meta({ id: "WebPushChannel" });
export const EmailChannel = z
  .strictObject({
    type: z.literal("email"),
    credential: credentialField,
    allow_fallback: allowFallbackField,
    payload: EmailPayload,
  })
  .meta({ id: "EmailChannel" });
export const SmsChannel = z
  .strictObject({
    type: z.literal("sms"),
    credential: credentialField,
    allow_fallback: allowFallbackField,
    payload: SmsPayload,
  })
  .meta({ id: "SmsChannel" });
export const WhatsappChannel = z
  .strictObject({
    type: z.literal("whatsapp"),
    credential: credentialField,
    allow_fallback: allowFallbackField,
    payload: WhatsappPayload,
  })
  .meta({ id: "WhatsappChannel" });

export const ChannelItem = z
  .discriminatedUnion("type", [
    FcmChannel,
    ApnsChannel,
    WebPushChannel,
    EmailChannel,
    SmsChannel,
    WhatsappChannel,
  ])
  .meta({ id: "ChannelItem" });
export type ChannelItem = z.infer<typeof ChannelItem>;

// ---------------------------------------------------------------------------
// Recipients
// ---------------------------------------------------------------------------

export const PushTokenAddress = z
  .strictObject({
    channel: z.enum(PUSH_CHANNELS),
    token: z.string().min(1).max(4096).meta({
      description:
        "Device address. FCM: the Firebase Installation ID (FID) from `onRegistered()`, or a registration token from the deprecated `getToken()`; both work (D-49).",
    }),
    credential: CredentialAlias.optional().meta({
      description:
        "Credential that issued this token (D-04). When empty, the channel item `credential` is used.",
    }),
  })
  .meta({ id: "PushTokenAddress" });

export const Addresses = z
  .strictObject({
    emails: z.array(z.email()).max(LIMITS.maxRecipientsPerRequest).optional(),
    phones: z.array(PhoneE164).max(LIMITS.maxRecipientsPerRequest).optional(),
    push_tokens: z.array(PushTokenAddress).max(LIMITS.maxRecipientsPerRequest).optional(),
  })
  .meta({
    id: "Addresses",
    description:
      "Ad-hoc addresses without a customer (D-10). `emails` feed the email channel, `phones` feed sms and whatsapp.",
  });

export const Recipients = z
  .strictObject({
    all_customers: z.boolean().optional().meta({
      description:
        "Broadcast to every customer of the app with an active address on at least one requested channel (D-52). Customers without an address for a channel get no delivery on it. Cannot be combined with other recipients and needs `category`.",
    }),
    customer_ids: z
      .array(z.string().min(1).max(255))
      .max(LIMITS.maxRecipientsPerRequest)
      .optional()
      .meta({ description: "`external_id` values owned by the client app (D-12)." }),
    segment_id: Uuid.optional().meta({ description: "Available in phase 2." }),
    addresses: Addresses.optional(),
  })
  .meta({ id: "Recipients" });

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

export const SendMessageRequest = z
  .strictObject({
    to: Recipients,
    channels: z.array(ChannelItem).min(1).max(LIMITS.maxChannelsPerRequest),
    strategy: z.enum(MESSAGE_STRATEGIES).default("parallel"),
    priority: z.enum(MESSAGE_PRIORITIES).default("normal").meta({
      description: "`high` for OTP and urgent transactional messages, `low` for broadcasts (P-05).",
    }),
    category: Category.optional(),
    options: z
      .strictObject({
        schedule_at: z.iso
          .datetime({ offset: true })
          .optional()
          .meta({ description: "Available in phase 2." }),
        fallback_timeout_sec: z.int().min(5).max(86_400).optional(),
      })
      .optional(),
    metadata: z
      .record(z.string().max(64), z.union([z.string().max(500), z.number(), z.boolean()]))
      .optional()
      .meta({ description: "Free-form data echoed back in responses and webhooks." }),
  })
  .superRefine((req, ctx) => {
    const seen = new Set<string>();
    req.channels.forEach((item, index) => {
      if (seen.has(item.type)) {
        ctx.addIssue({
          code: "custom",
          path: ["channels", index, "type"],
          message: `Channel ${item.type} appears more than once`,
        });
      }
      seen.add(item.type);
    });
    const to = req.to;
    const a = to.addresses;
    const hasOthers =
      (to.customer_ids?.length ?? 0) > 0 ||
      Boolean(to.segment_id) ||
      (a?.emails?.length ?? 0) + (a?.phones?.length ?? 0) + (a?.push_tokens?.length ?? 0) > 0;
    if (!hasOthers && !to.all_customers) {
      ctx.addIssue({
        code: "custom",
        path: ["to"],
        message: "Provide at least one of all_customers, customer_ids, segment_id or addresses",
      });
    }
    if (to.all_customers) {
      if (hasOthers) {
        ctx.addIssue({
          code: "custom",
          path: ["to", "all_customers"],
          message: "all_customers cannot be combined with customer_ids, segment_id or addresses",
        });
      }
      if (!req.category) {
        ctx.addIssue({
          code: "custom",
          path: ["category"],
          message: "category is required when all_customers is used",
        });
      }
    }
    if (to.segment_id && !req.category) {
      ctx.addIssue({
        code: "custom",
        path: ["category"],
        message: "category is required when segment_id is used",
      });
    }
  })
  .meta({
    id: "SendMessageRequest",
    example: {
      to: {
        addresses: {
          emails: ["erry@aiyo.id"],
          push_tokens: [{ channel: "fcm", token: "fcm-device-token", credential: "fcm-main" }],
        },
      },
      priority: "high",
      channels: [
        { type: "fcm", payload: { title: "Order shipped", body: "Order #123 is on its way" } },
        {
          type: "email",
          credential: "email-main",
          payload: { subject: "Order #123 shipped", text: "Your order is on its way." },
        },
      ],
      metadata: { order_id: "123" },
    },
  });
export type SendMessageRequest = z.infer<typeof SendMessageRequest>;
export type SendMessageInput = z.input<typeof SendMessageRequest>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const MessageCreated = z
  .object({
    id: Uuid,
    status: z.enum(MESSAGE_STATUSES),
    test_mode: z.boolean(),
    idempotent_replay: z.boolean(),
    created_at: IsoDate,
  })
  .meta({ id: "MessageCreated" });
export type MessageCreated = z.infer<typeof MessageCreated>;

export const DeliveryCounts = z
  .object({
    total: z.int(),
    pending: z.int(),
    sent: z.int().meta({ description: "Accepted by the provider, including delivered ones." }),
    delivered: z.int(),
    failed: z.int(),
    skipped: z.int(),
  })
  .meta({ id: "DeliveryCounts" });
export type DeliveryCounts = z.infer<typeof DeliveryCounts>;

export const MessageSummary = z
  .object({
    id: Uuid,
    app_id: Uuid,
    status: z.enum(MESSAGE_STATUSES),
    strategy: z.enum(MESSAGE_STRATEGIES),
    priority: z.enum(MESSAGE_PRIORITIES),
    category: z.string(),
    test_mode: z.boolean(),
    channels: z.array(z.enum(CHANNELS)),
    preview: z
      .string()
      .nullable()
      .meta({ description: "Title, subject or text of the first channel item, for lists." }),
    counts: DeliveryCounts,
    metadata: z.record(z.string(), z.unknown()).nullable(),
    created_at: IsoDate,
    started_at: IsoDate.nullable(),
    completed_at: IsoDate.nullable(),
  })
  .meta({ id: "MessageSummary" });
export type MessageSummary = z.infer<typeof MessageSummary>;

export const MessageAudience = z
  .object({
    all_customers: z.boolean(),
    customers: z.int().meta({ description: "Number of `customer_ids` in the request." }),
    emails: z.int(),
    phones: z.int(),
    push_tokens: z.int(),
  })
  .meta({ id: "MessageAudience", description: "Who the request addressed, as counts." });
export type MessageAudience = z.infer<typeof MessageAudience>;

export const MessageDetail = MessageSummary.extend({
  per_channel: z.record(z.string(), DeliveryCounts),
  audience: MessageAudience,
  content: z
    .array(ChannelItem)
    .meta({ description: "The channel items of the request, with the payload sent on each channel." }),
}).meta({ id: "MessageDetail" });
export type MessageDetail = z.infer<typeof MessageDetail>;

export const MessageList = paginated(MessageSummary, "MessageList");
export type MessageList = z.infer<typeof MessageList>;

export const Delivery = z
  .object({
    id: Uuid,
    message_id: Uuid,
    channel: z.enum(CHANNELS),
    address: z.string().meta({ description: "Empty when the recipient had no address for this channel." }),
    customer_id: Uuid.nullable(),
    external_id: z
      .string()
      .nullable()
      .meta({ description: "Customer external id when sent via `customer_ids`." }),
    status: z.enum(DELIVERY_STATUSES),
    issue: z.enum(DELIVERY_ISSUES).meta({
      description: "Plain-language outcome class derived from status and error code (D-38).",
    }),
    attempts: z.int(),
    credential_alias: z.string().nullable(),
    provider: z.string().nullable().meta({ description: "Code of the provider used, e.g. `fcm` or `smtp`." }),
    provider_message_id: z.string().nullable(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
    queued_at: IsoDate,
    sent_at: IsoDate.nullable(),
    delivered_at: IsoDate.nullable(),
    failed_at: IsoDate.nullable(),
  })
  .meta({ id: "Delivery" });
export type Delivery = z.infer<typeof Delivery>;

export const DeliveryList = paginated(Delivery, "DeliveryList");
export type DeliveryList = z.infer<typeof DeliveryList>;

export const DeliveryListQuery = z.object({
  status: z.enum(DELIVERY_STATUSES).optional(),
  channel: z.enum(CHANNELS).optional(),
});

export const DeliveryEvent = z
  .object({
    id: Uuid,
    type: z.enum(DELIVERY_EVENT_TYPES),
    source: z.string(),
    payload: z.record(z.string(), z.unknown()).nullable(),
    occurred_at: IsoDate,
  })
  .meta({ id: "DeliveryEvent" });
export type DeliveryEvent = z.infer<typeof DeliveryEvent>;

export const DeliveryDetail = Delivery.extend({
  rendered: z.record(z.string(), z.unknown()).nullable(),
  credential_id: Uuid.nullable(),
  subscription_id: Uuid.nullable(),
  events: z.array(DeliveryEvent),
}).meta({ id: "DeliveryDetail" });
export type DeliveryDetail = z.infer<typeof DeliveryDetail>;
